import { useEffect, useMemo, useRef } from 'react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import type { Scene } from './scenes/types';

/**
 * A backdrop, with its particle fields seeded once per visit.
 *
 * The scenes are the concept's own markup, inserted verbatim — they are static
 * artwork with no interactive parts, and hand-transcribing 440 KB of generated
 * SVG into JSX would be a lot of opportunity to introduce differences. Nothing
 * here is user-supplied: it is all build-time output of `tools/port_concepts.py`.
 */
export function Scenery({ scene }: { scene: Scene | null }) {
  const host = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  const html = useMemo(() => {
    if (!scene) return '';
    // Particle hosts are empty divs in the markup; fill them before the string
    // is inserted, so the scene arrives complete rather than being patched a
    // frame later. The scene knows the motion preference and skips its
    // animation-only fields under it — but static furniture (clouds, birds)
    // is still seeded, for an effect to place.
    let out = scene.markup;
    for (const [id, inner] of Object.entries(scene.particles(window.innerWidth, reduced))) {
      // The host may carry other attributes (mystery's rain sits on a
      // classed div), so match the id among them. Function replacement, not a
      // string: `$&` and friends are substitution patterns to String.replace,
      // and generated markup must go in verbatim.
      out = out.replace(
        new RegExp(`<div([^>]*?)id="${id}"([^>]*?)></div>`),
        (_m, pre: string, post: string) => `<div${pre}id="${id}"${post}>${inner}</div>`,
      );
    }
    return out;
  }, [scene, reduced]);

  // Effects run whether or not motion is reduced: one of them is the concourse
  // wall clock, which has to keep telling the time either way. Any *animation*
  // an effect starts is still cut by the global reduced-motion rule in
  // styles/global.css, so this does not smuggle movement past the preference.
  useEffect(() => {
    if (!scene?.effect || !host.current) return;
    return scene.effect(host.current) ?? undefined;
  }, [scene, html]);

  return <div ref={host} dangerouslySetInnerHTML={{ __html: html }} />;
}
